/**
 * Bingo Cell Component
 *
 * Displays a single cell on a bingo card with status indicators.
 */

import { useState } from 'react';
import type { BingoCell } from '@/types/bingo';
import { Check, Circle, X } from 'lucide-react';
import clsx from 'clsx';

interface BingoCellProps {
  cell: BingoCell;
  onClick?: (cell: BingoCell) => void;
  onComplete?: (cell: BingoCell) => void;
  size?: 'sm' | 'md' | 'lg';
  showPattern?: boolean; // Highlight if part of completed pattern
}

const statusColors = {
  not_started: 'bg-gradient-to-br from-white to-gray-50 border-gray-300 text-gray-700',
  in_progress: 'bg-gradient-to-br from-blue-100 to-blue-200 border-blue-400 text-blue-900',
  completed: 'bg-gradient-to-br from-green-100 via-emerald-100 to-teal-100 border-green-500 text-green-900',
  abandoned: 'bg-gradient-to-br from-red-50 to-red-100 border-red-300 text-red-800',
};

const statusIcons = {
  not_started: <Circle className="w-4 h-4" />,
  in_progress: <Circle className="w-4 h-4 fill-current" />,
  completed: <Check className="w-5 h-5" />,
  abandoned: <X className="w-4 h-4" />,
};

const sizeClasses = {
  sm: 'p-2 text-xs',
  md: 'p-3 text-xs',
  lg: 'p-4 text-sm',
};

export function BingoCellComponent({
  cell,
  onClick,
  onComplete,
  size = 'md',
  showPattern = false,
}: BingoCellProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (onClick) {
      onClick(cell);
    } else if (onComplete && cell.status !== 'completed') {
      onComplete(cell);
    }
  };

  const isCompleted = cell.status === 'completed';
  const isClickable = onClick || (onComplete && !isCompleted);

  return (
    <div
      className={clsx(
        'relative border-2 rounded-lg transition-all duration-300 ease-in-out',
        statusColors[cell.status],
        sizeClasses[size],
        isClickable && 'cursor-pointer hover:shadow-xl hover:scale-[1.05] hover:-translate-y-0.5',
        showPattern && isCompleted && 'ring-4 ring-yellow-400 ring-opacity-70 shadow-lg',
        isCompleted && 'font-semibold shadow-md',
        'w-full h-full min-h-[80px]',
        'overflow-hidden'
      )}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={`Cell ${cell.row + 1}, ${cell.column + 1}: ${cell.goalTitle}`}
    >
      {/* Status icon with better positioning */}
      <div className={clsx(
        "absolute top-1.5 right-1.5",
        isCompleted ? "opacity-90" : "opacity-50"
      )}>
        {statusIcons[cell.status]}
      </div>

      {/* Completed badge */}
      {isCompleted && (
        <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full w-6 h-6 flex items-center justify-center shadow-lg transform rotate-12">
          <span className="text-white text-sm font-bold">✓</span>
        </div>
      )}

      {/* Cell content */}
      <div className="flex flex-col h-full justify-center pr-5">
        <p className="font-semibold leading-tight line-clamp-4 text-xs">
          {cell.goalTitle}
        </p>
        {cell.goalDescription && size !== 'sm' && (
          <p className="text-xs mt-0.5 opacity-70 line-clamp-1 leading-tight">
            {cell.goalDescription}
          </p>
        )}
      </div>

      {/* Hover overlay for completion with gradient */}
      {isHovered && !isCompleted && onComplete && (
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center backdrop-blur-[2px]">
          <div className="bg-white/90 px-2.5 py-1 rounded-full shadow-md">
            <span className="text-xs font-semibold text-purple-700">
              ✓ Complete
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

